import { DialogModule } from '@angular/cdk/dialog';
import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideRouter, Router, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@environment/environment';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { createFilteredSentryErrorHandler } from '@shared/utils/sentry-error-handler.util';
import { shouldDropSentryEvent } from '@shared/utils/sentry-event-filter.util';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { provideMatomoAnalytics } from './app/providers/matomo.providers';

if (environment.production && environment.sentryDsn) {
  Sentry.init({
    environment: environment.production ? 'production' : 'development',
    dsn: environment.sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })
    ],
    release: environment.version,
    beforeSend: (event, hint) => (shouldDropSentryEvent(event, hint) ? null : event),
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Performance Monitoring
    tracesSampleRate: 0.3, //  Capture 100% of the transactions
    tracePropagationTargets: [/^https:\/\/pis\.wuestenrot\.at/, /^https:\/\/staging\.pis\.wuestenrot\.at/]
  });
  enableProdMode();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  window.console.log = (): void => {};
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideHttpClient(withXhr()),
    provideOAuthClient(),
    provideRouter(appRoutes, withViewTransitions(), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    importProvidersFrom(BrowserModule, DialogModule, ToastrModule.forRoot()),
    { provide: ErrorHandler, useValue: createFilteredSentryErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideServiceWorker('ngsw-worker.js', { enabled: !isDevMode(), registrationStrategy: 'registerWhenStable:30000' }),
    ...provideMatomoAnalytics()
  ]
}).catch(err => console.error(err));
